import { createContext, useState, useContext, useCallback } from 'react';

const TourContext = createContext();

export const TourProvider = ({ children }) => {
  const [runTour, setRunTour] = useState(false);
  const [steps, setSteps] = useState([]);
  const [completedActions, setCompletedActions] = useState([]);
  
  /** some helper function for interactive tour */
  const waitForElement = useCallback((selector, timeout = 5000) => {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(true);
      }

      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, timeout);
    });
  }, []);


  const waitForValue = useCallback(async (selector, minLength = 1) => {
    await waitForElement(selector);
    const element = document.querySelector(selector);
    
    return new Promise((resolve) => {
      if (element.value && element.value.length >= minLength) {
        return resolve(true);
      }

      const handler = () => {
        if (element.value.length >= minLength) {
          element.removeEventListener('input', handler);
          resolve(true);
        }
      };

      element.addEventListener('input', handler);
    });
  }, [waitForElement]);


  const waitForClick = useCallback(async (selector) => {
    await waitForElement(selector);
    const element = document.querySelector(selector);
    
    return new Promise((resolve) => {
      const handler = () => {
        element.removeEventListener('click', handler);
        resolve(true);
      };
      element.addEventListener('click', handler);
    });
  }, [waitForElement]);


  const completeAction = useCallback((actionId) => {
    setCompletedActions(prev => [...prev, actionId]);
  }, []);


  const resetTour = useCallback(() => {
    setRunTour(false);
    setCompletedActions([]);
  }, []);

  return (
    <TourContext.Provider 
      value={{
        // State
        runTour,
        steps,
        completedActions,
        
        // Setters
        setRunTour,
        setSteps,
        
        // Interactive functions
        waitForElement,
        waitForValue,
        waitForClick,
        completeAction,
        resetTour
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) throw new Error('useTour must be used within TourProvider');
  return context;
};